import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCheck, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [contactInfo, setContactInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/services/${slug}`),
      axios.get(`${API_URL}/contact`)
    ])
      .then(([serviceResponse, contactResponse]) => {
        console.log('Service loaded:', serviceResponse.data);
        console.log('Contact loaded:', contactResponse.data);
        setService(serviceResponse.data);
        setContactInfo(contactResponse.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading:', error);
        setError('Service not found');
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <section className="pt-32 pb-20 bg-navy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <div className="text-cyan text-xl">Loading...</div>
        </div>
      </section>
    );
  }

  if (error || !service) {
    return (
      <section className="pt-32 pb-20 bg-navy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-white">Service Not Found</h1>
          <p className="text-gray-400 mt-4">The service you're looking for doesn't exist.</p>
          <Link to="/services" className="inline-block mt-6 text-cyan hover:underline">← Back to Services</Link>
        </div>
      </section>
    );
  }

  // Parse features
  let features = service.features;
  if (typeof features === 'string') {
    try { features = JSON.parse(features); } catch(e) { features = []; }
  }
  if (!Array.isArray(features)) features = [];

  const whatsappNumber = contactInfo.whatsapp || '+1234567890';
  const cleanWhatsApp = whatsappNumber.replace(/[^0-9]/g, '');

  return (
    <section className="pt-32 pb-20 bg-navy min-h-screen">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          <Link to="/services" className="text-cyan hover:underline inline-flex items-center gap-2">
            <FaArrowLeft /> Back to Services
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-6">{service.title}</h1>
          <p className="text-gray-400 text-lg mt-4">{service.description}</p>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-darkNavy rounded-xl p-6 border border-gray-800">
              <h3 className="text-cyan font-semibold">Duration</h3>
              <p className="text-white mt-1">{service.duration || 'Contact for pricing'}</p>
            </div>
            <div className="bg-darkNavy rounded-xl p-6 border border-gray-800">
              <h3 className="text-cyan font-semibold">Price</h3>
              <p className="text-white mt-1">{service.price || 'Contact for pricing'}</p>
            </div>
          </div>

          <div className="mt-8 bg-darkNavy rounded-xl p-6 border border-gray-800">
            <h3 className="text-xl font-semibold text-white">About This Service</h3>
            <p className="text-gray-400 mt-3">{service.fullDescription || service.description}</p>
          </div>

          {features.length > 0 && (
            <div className="mt-8 bg-darkNavy rounded-xl p-6 border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-4">Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-300">
                    <FaCheck className="text-cyan text-sm" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 p-6 bg-cyan/10 rounded-xl border border-cyan/20">
            <h3 className="text-xl font-semibold text-cyan">Ready to Get Started?</h3>
            <p className="text-gray-400 mt-2">Contact us to learn more about this service and how we can help your business.</p>
            <div className="flex flex-wrap gap-4 mt-4">
              <Link to="/contact" className="px-6 py-2 bg-cyan text-navy rounded-full font-semibold hover:bg-cyan/80 transition inline-flex items-center gap-2">
                <FaEnvelope /> Contact Us
              </Link>
              <a href={`https://wa.me/${cleanWhatsApp}`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 border border-cyan text-cyan rounded-full font-semibold hover:bg-cyan/10 transition inline-flex items-center gap-2">
                <FaWhatsapp /> WhatsApp
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceDetail;
